import { ElementType, forwardRef, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowDropDown, ArrowRight, Label } from '@mui/icons-material'
import { Box, styled, Typography, useTheme } from '@mui/material'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import {
  TreeItem2Content,
  TreeItem2GroupTransition,
  TreeItem2IconContainer,
  TreeItem2Root,
} from '@mui/x-tree-view/TreeItem2'
import { TreeItem2Icon } from '@mui/x-tree-view/TreeItem2Icon'
import { TreeItem2Provider } from '@mui/x-tree-view/TreeItem2Provider'
import {
  useTreeItem2,
  UseTreeItem2Parameters,
} from '@mui/x-tree-view/useTreeItem2'
import clsx from 'clsx'
import { uniq } from 'lodash'
import { LinkType } from './Link.type'
import { TreeProps } from './TreeProps.type'

declare module 'react' {
  interface CSSProperties {
    '--tree-view-color'?: string
    '--tree-view-bg-color'?: string
    // '--TreeView-itemChildrenIndentation'?: number
  }
}

interface StyledTreeItemProps
  extends
    Omit<UseTreeItem2Parameters, 'rootRef'>,
    React.HTMLAttributes<HTMLLIElement> {
  labelIcon: ElementType
  labelInfo?: string
  path?: string
}

const CustomTreeItemRoot = styled(TreeItem2Root)(({ theme }) => ({
  color: theme.palette.text.secondary,
}))

const CustomTreeItemContent = styled(TreeItem2Content)(({ theme }) => ({
  marginBottom: theme.spacing(0.3),
  color: theme.palette.text.primary,
  borderRadius: 0,
  paddingRight: theme.spacing(1),
  fontWeight: theme.typography.fontWeightMedium,
  gap: 0,
  '&.expanded': {
    fontWeight: theme.typography.fontWeightRegular,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.focused, &.selected, &.selected.focused': {
    backgroundColor: `var(--tree-view-bg-color, ${theme.palette.action.selected})`,
    color: 'var(--tree-view-color)',
  },
}))

const CustomTreeItemIconContainer = styled(TreeItem2IconContainer)(
  ({ theme }) => ({
    marginRight: theme.spacing(0),
  })
)

const CustomTreeItemGroupTransition = styled(TreeItem2GroupTransition)(
  ({ theme }) => ({
    marginLeft: 0,
    [`& .content`]: {
      paddingLeft: theme.spacing(2),
    },
  })
)

const CustomTreeItem = forwardRef(function CustomTreeItem(
  props: StyledTreeItemProps,
  ref: React.Ref<HTMLLIElement>
) {
  const theme = useTheme()
  const {
    id,
    itemId,
    label,
    disabled,
    children,
    labelIcon: LabelIcon,
    labelInfo,
    ...other
  } = props

  const {
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getLabelProps,
    getGroupTransitionProps,
    status,
  } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref })

  const style = {
    '--tree-view-color': theme.palette.text.primary,
    '--tree-view-bg-color': theme.palette.background.default,
    // '--TreeView-itemChildrenIndentation': 0,
  }

  return (
    <TreeItem2Provider itemId={itemId}>
      <CustomTreeItemRoot {...getRootProps({ ...other, style })}>
        <CustomTreeItemContent
          {...getContentProps({
            className: clsx('content', {
              expanded: status.expanded,
              selected: status.selected,
              focused: status.focused,
            }),
          })}
        >
          <CustomTreeItemIconContainer {...getIconContainerProps()}>
            <TreeItem2Icon status={status} />
          </CustomTreeItemIconContainer>
          <Box
            sx={{
              display: 'flex',
              flexGrow: 1,
              alignItems: 'center',
              p: 0.5,
              pr: 0,
            }}
          >
            <Box className="opacity-40" component={LabelIcon} sx={{ mr: 1 }} />
            <Typography
              component="span"
              {...getLabelProps({
                variant: 'body2',
                sx: { display: 'flex', fontWeight: 'inherit', flexGrow: 1 },
              })}
            />
            <Typography component="span" variant="caption" color="inherit">
              {labelInfo}
            </Typography>
          </Box>
        </CustomTreeItemContent>
        {children && (
          <CustomTreeItemGroupTransition {...getGroupTransitionProps()} />
        )}
      </CustomTreeItemRoot>
    </TreeItem2Provider>
  )
})

const EndIcon = () => {
  return <div style={{ width: 24 }} />
}

type TreeItemType = LinkType & { parentId?: string }

const Tree = (props: TreeProps) => {
  const { items } = props
  const location = useLocation()
  const navigate = useNavigate()

  const { paths, tree } = useMemo(() => {
    const paths: Record<string, string> = {}
    const createTreeItem = (item: LinkType) => {
      if (item.path) {
        paths[item.id] = item.path
      }
      const children = createItems(item)
      return (
        <CustomTreeItem
          key={item.id}
          itemId={item.id}
          label={item.title}
          labelIcon={item.icon ?? Label}
          path={item.path}
        >
          {children}
        </CustomTreeItem>
      )
    }
    const createItems = (item: LinkType) => {
      return item.children?.map((child: LinkType) => {
        return createTreeItem(child)
      })
    }
    const tree = createItems({ children: items } as LinkType)
    return { paths, tree }
  }, [items])

  const [expandedItems, setExpandedItems] = useState<string[]>([])
  useEffect(() => {
    const findExpandesItems = (item: TreeItemType): boolean => {
      for (const child of item.children ?? []) {
        if (findExpandesItems(child)) {
          expandedItems.push(child.id)
          return true
        }
        if (child.path === location.pathname) {
          return true
        }
      }
      return false
    }
    findExpandesItems({ children: items } as TreeItemType)
    setExpandedItems(uniq(expandedItems))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, location.pathname])

  const [selectedItem, setSelectedItem] = useState<string>('')
  useEffect(() => {
    const index = Object.values(paths).findIndex(
      (path) => path === location.pathname
    )
    setSelectedItem(index >= 0 ? Object.keys(paths)[index] : '')
  }, [location.pathname, paths])

  return (
    <SimpleTreeView
      aria-label="main-navigation"
      expandedItems={expandedItems}
      selectedItems={selectedItem}
      slots={{
        expandIcon: ArrowRight,
        collapseIcon: ArrowDropDown,
        endIcon: EndIcon,
      }}
      sx={{ flexGrow: 1, maxWidth: 400 }}
      onExpandedItemsChange={(_, expandedItems) => {
        setExpandedItems(expandedItems)
      }}
      onItemClick={(_, itemId) => {
        const path = paths[itemId]
        if (path) {
          navigate(path)
        }
      }}
    >
      {tree}
    </SimpleTreeView>
  )
}

export default Tree
